// War Ledger (GDD §6, §14): running totals by category from private state, plus deploys by unit type counted from the
// player's own unitDeployed events. It drops down under the HUD's right column from its own toggle and never covers
// it, so the toggle always closes it; Escape and the drawn ✕ close it too. The sheet is a canvas hologram that
// ledgerPaint.ts paints as a function of the ledger's clock. The war screen's single frame loop advances that clock
// (HudHandle.update): the canvas repaints only while something moves, and never while closed. Figures update while
// the sheet is hidden (only on change-driven input) and jump there; while it is open they roll. A visually hidden
// text copy carries the same figures for screen readers.

import { UNIT_KEYS, type UnitKey } from '@taxwar/content'
import type { PlayerPrivateState } from '@taxwar/net'
import type { VisualSettings } from '../render/visualSettings'
import { UNIT_PRESENTATION } from './catalog'
import { button, el, TextSlot } from './dom'
import { HudError } from './errors'
import { formatMoney } from './format'
import {
  CLOSE_BOX,
  FIGURE_KEYS,
  FOLD_SEC,
  LEDGER_COPY,
  LEDGER_HEIGHT,
  LEDGER_WIDTH,
  LIVE_CUE_SEC,
  OPEN_CHOREOGRAPHY_SEC,
  OPEN_STAMP_DELAY_SEC,
  STAMP_SEC,
  LedgerPainter,
  figurePending,
  formatCount,
  formatFigure,
  unfoldAt,
  type DeployCell,
  type FigureKey,
  type SheetState,
} from './ledgerPaint'
import { costPerCityCaptured, warSpend } from './ledgerMath'
import { SPRINGS, Track } from './motion'

const BUTTON_LABEL = 'Ledger'
const ELEMENT_ID = 'tw-ledger'
/** Canvas backing stores stop at this device pixel ratio: crisp text, bounded memory. */
const MAX_PIXEL_RATIO = 2
/** Space kept between the ledger's foot and the HUD's bottom edge. */
const FLOOR_CLEARANCE_PX = 8
/**
 * Short views shrink the sheet to fit, but never below this scale, where its 10 px labels would stop being legible.
 * While open it may cover part of the Arsenal bar on views too short for both (its hotkeys still work).
 */
const MIN_FIT_SCALE = 0.75
const NEVER = Number.NEGATIVE_INFINITY
/** stampAt while nothing is billed: the stamp never lands. */
const NOT_BILLED = Number.POSITIVE_INFINITY

/** Accessible copy: a label per figure (the canvas paints the same labels, upper-cased). */
const FIGURE_LABELS: Readonly<Record<FigureKey, string>> = {
  warSpend: LEDGER_COPY.spend,
  spentUnits: LEDGER_COPY.units,
  spentBuildings: LEDGER_COPY.buildings,
  spentStrikes: LEDGER_COPY.strikes,
  lostValue: LEDGER_COPY.lost,
  citiesCaptured: LEDGER_COPY.captured,
  citiesLost: LEDGER_COPY.misplaced,
  perCity: LEDGER_COPY.perCity,
}

export interface LedgerOptions {
  readonly settings: VisualSettings
  /** Called as the ledger opens, so the other drop-downs close. */
  readonly onOpen: () => void
  /** Viewport y (CSS px) the ledger's foot stays above: the HUD's bottom edge. */
  readonly floorY: () => number
  readonly signal: AbortSignal
}

/** What the painter reads, with the fields only this panel writes. */
interface LedgerState {
  readonly figures: Readonly<Record<FigureKey, Track>>
  readonly changedAt: Record<FigureKey, number>
  readonly deploys: readonly DeployCell[]
  sheet: SheetState
  stampAt: number
}

function figureTracks(): Record<FigureKey, Track> {
  const figures = {} as Record<FigureKey, Track>
  for (const key of FIGURE_KEYS) figures[key] = new Track(SPRINGS.roll, 0)
  return figures
}

function figureTimes(): Record<FigureKey, number> {
  const times = {} as Record<FigureKey, number>
  for (const key of FIGURE_KEYS) times[key] = NEVER
  return times
}

export class LedgerPanel {
  readonly element: HTMLElement
  readonly toggleButton: HTMLButtonElement
  private readonly canvas: HTMLCanvasElement
  private readonly context: CanvasRenderingContext2D
  private readonly painter = new LedgerPainter()
  private readonly state: LedgerState
  private readonly figureText: Readonly<Record<FigureKey, TextSlot>>
  private readonly deployText: readonly TextSlot[]
  /** Seconds of frames seen by this ledger: the time its motion is a function of. */
  private clock = 0
  private reduceMotion: boolean
  /** Ledger time until which something moves; the canvas repaints every frame until then, and once after. */
  private animatedUntil = NEVER
  /** A repaint is due although nothing moves (a figure jumped, a resize, a theme change). */
  private dirty = false
  private fitScale = 1
  private pixelRatio = 1

  constructor(private readonly options: LedgerOptions) {
    const signal = options.signal
    this.reduceMotion = options.settings.reduceEffects
    this.state = {
      figures: figureTracks(),
      changedAt: figureTimes(),
      deploys: UNIT_KEYS.map((unit) => ({ unit, count: 0, spent: new Track(SPRINGS.roll, 0), poppedAt: NEVER })),
      sheet: { kind: 'closed' },
      stampAt: NOT_BILLED,
    }

    this.element = el('section', 'tw-ledger')
    this.element.id = ELEMENT_ID
    this.element.setAttribute('aria-label', 'War Ledger')
    this.element.hidden = true
    this.canvas = el('canvas', 'tw-ledger__canvas')
    this.canvas.setAttribute('aria-hidden', 'true')
    const context = this.canvas.getContext('2d')
    if (context === null) throw new HudError('no 2D canvas context for the War Ledger')
    this.context = context
    this.canvas.addEventListener('click', this.onCanvasClick, { signal })
    this.canvas.addEventListener('pointermove', this.onCanvasPointerMove, { signal })

    const copy = el('dl', 'tw-visually-hidden')
    const figureText = {} as Record<FigureKey, TextSlot>
    for (const key of FIGURE_KEYS) {
      const value = el('dd', '')
      copy.append(el('dt', '', FIGURE_LABELS[key]), value)
      figureText[key] = new TextSlot(value)
    }
    this.figureText = figureText
    this.deployText = UNIT_KEYS.map((key) => {
      const value = el('dd', '')
      copy.append(el('dt', '', `${LEDGER_COPY.deploys}: ${UNIT_PRESENTATION[key].name}`), value)
      return new TextSlot(value)
    })
    for (const key of FIGURE_KEYS) this.renderFigureText(key)
    this.state.deploys.forEach((cell, i) => this.renderDeployText(cell, i))
    this.element.append(this.canvas, copy)

    this.toggleButton = button('tw-button tw-hud__ledger-toggle', BUTTON_LABEL)
    this.toggleButton.setAttribute('aria-controls', ELEMENT_ID)
    this.toggleButton.setAttribute('aria-expanded', 'false')
    this.toggleButton.addEventListener('click', () => this.setOpen(!this.open), { signal })
    window.addEventListener('resize', this.onResize, { signal })
  }

  /** Open, and not folding away (a folding sheet reopens on the next toggle). */
  get open(): boolean {
    return this.state.sheet.kind === 'open'
  }

  setOpen(open: boolean): void {
    if (open === this.open) return
    if (open) this.unfold()
    else this.fold()
    this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false')
  }

  /** "Reduce effects": a reduced ledger shows every state settled, with no motion. */
  applySettings(settings: VisualSettings): void {
    this.reduceMotion = settings.reduceEffects
    this.dirty = true
  }

  /** The theme's colours changed (a new palette): the canvas repaints with them. */
  themeChanged(): void {
    this.painter.invalidatePalette()
    this.dirty = true
  }

  update(state: PlayerPrivateState): void {
    const ledger = state.ledger
    const spend = warSpend(ledger)
    this.setFigure('spentUnits', ledger.spentUnits)
    this.setFigure('spentBuildings', ledger.spentBuildings)
    this.setFigure('spentStrikes', ledger.spentStrikes)
    this.setFigure('warSpend', spend)
    this.setFigure('lostValue', ledger.lostValue)
    this.setFigure('citiesCaptured', ledger.citiesCaptured)
    this.setFigure('citiesLost', ledger.citiesLost)
    this.setFigure('perCity', costPerCityCaptured(ledger) ?? 0)
    // The first bill of the war stamps an open sheet on the spot.
    if (this.open && spend > 0 && this.state.stampAt === NOT_BILLED) {
      this.state.stampAt = this.clock
      this.animate(STAMP_SEC)
    }
  }

  recordDeploy(unit: UnitKey, cost: number): void {
    const index = UNIT_KEYS.indexOf(unit)
    const cell = this.state.deploys[index]
    if (cell === undefined) throw new HudError(`no ledger cell for unit ${unit}`)
    cell.count++
    const spent = cell.spent.target + cost
    if (this.changesAnimate()) {
      cell.spent.set(spent, this.clock)
      cell.poppedAt = this.clock
      this.animate(LIVE_CUE_SEC)
    } else {
      cell.spent.snap(spent)
      this.dirty = true
    }
    this.renderDeployText(cell, index)
  }

  /** Once per frame (the war screen's loop): advances the ledger clock and repaints while anything moves. */
  frame(dtSeconds: number): void {
    this.clock += dtSeconds
    const sheet = this.state.sheet
    if (sheet.kind === 'closed') return
    if (sheet.kind === 'closing' && this.clock - sheet.since >= FOLD_SEC) {
      this.hide()
      return
    }
    // The first frame past animatedUntil still paints, so the settled state is always drawn.
    if (!this.dirty && this.clock - dtSeconds > this.animatedUntil) return
    this.dirty = false
    const ratio = this.pixelRatio * this.fitScale
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0)
    this.painter.paint(this.context, this.element, this.state, this.reduceMotion ? Number.POSITIVE_INFINITY : this.clock)
  }

  // -------------------------------------------------------------------------------------------- lifecycle

  private unfold(): void {
    this.options.onOpen()
    this.element.hidden = false
    this.fit()
    const now = this.clock
    this.state.sheet = { kind: 'open', since: now }
    for (const key of FIGURE_KEYS) this.state.changedAt[key] = NEVER
    for (const cell of this.state.deploys) cell.poppedAt = NEVER
    this.state.stampAt = this.state.figures.warSpend.target > 0 ? now + OPEN_STAMP_DELAY_SEC : NOT_BILLED
    this.animate(OPEN_CHOREOGRAPHY_SEC)
  }

  private fold(): void {
    const sheet = this.state.sheet
    if (sheet.kind !== 'open') return
    if (this.reduceMotion) {
      this.hide()
      return
    }
    this.state.sheet = { kind: 'closing', since: this.clock, from: Math.min(1, unfoldAt(this.clock - sheet.since)) }
    this.animate(FOLD_SEC)
  }

  private hide(): void {
    this.state.sheet = { kind: 'closed' }
    this.element.hidden = true
  }

  /** Keeps the canvas painting for `seconds` from now; a reduced ledger just repaints once, settled. */
  private animate(seconds: number): void {
    if (this.reduceMotion) this.dirty = true
    else this.animatedUntil = Math.max(this.animatedUntil, this.clock + seconds)
  }

  /** Whether a change animates: the sheet is open and motion is allowed (else it jumps to its new value). */
  private changesAnimate(): boolean {
    return this.open && !this.reduceMotion
  }

  private setFigure(key: FigureKey, value: number): void {
    const track = this.state.figures[key]
    if (value === track.target) return
    if (this.changesAnimate()) {
      track.set(value, this.clock)
      this.state.changedAt[key] = this.clock
      this.animate(LIVE_CUE_SEC)
    } else {
      track.snap(value)
      this.dirty = true
    }
    this.renderFigureText(key)
  }

  // -------------------------------------------------------------------------------------------- canvas

  /** Sizes the canvas to the room below its top (it hangs under the controls, never over them). */
  private fit(): void {
    const top = this.element.getBoundingClientRect().top
    const room = this.options.floorY() - FLOOR_CLEARANCE_PX - top
    this.fitScale = Math.min(1, Math.max(MIN_FIT_SCALE, room / LEDGER_HEIGHT))
    this.pixelRatio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO)
    const cssWidth = LEDGER_WIDTH * this.fitScale
    const cssHeight = LEDGER_HEIGHT * this.fitScale
    this.canvas.style.width = `${cssWidth}px`
    this.canvas.style.height = `${cssHeight}px`
    this.canvas.width = Math.round(cssWidth * this.pixelRatio)
    this.canvas.height = Math.round(cssHeight * this.pixelRatio)
    this.dirty = true
  }

  private readonly onResize = (): void => {
    if (this.state.sheet.kind !== 'closed') this.fit()
  }

  private hitsClose(event: MouseEvent): boolean {
    const x = event.offsetX / this.fitScale
    const y = event.offsetY / this.fitScale
    return x >= CLOSE_BOX.x && x <= CLOSE_BOX.x + CLOSE_BOX.size && y >= CLOSE_BOX.y && y <= CLOSE_BOX.y + CLOSE_BOX.size
  }

  private readonly onCanvasClick = (event: MouseEvent): void => {
    if (this.hitsClose(event)) this.setOpen(false)
  }

  private readonly onCanvasPointerMove = (event: PointerEvent): void => {
    this.canvas.classList.toggle('is-over-close', this.hitsClose(event))
  }

  // -------------------------------------------------------------------------------------------- accessible copy

  private renderFigureText(key: FigureKey): void {
    const text = figurePending(key, this.state) ? LEDGER_COPY.noCaptures : formatFigure(key, this.state.figures[key].target)
    this.figureText[key].set(text)
  }

  private renderDeployText(cell: DeployCell, index: number): void {
    ;(this.deployText[index] as TextSlot).set(`${formatCount(cell.count)}, ${formatMoney(cell.spent.target)}`)
  }
}
